import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { TFunction } from 'i18next';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FLEETCARE_BG_CLASS, MINDY_BG_CLASS, OMS_BG_CLASS } from './backgrounds';
import logo_claro from '../assets/logo_claro.svg';
import qm_fleetcare from '../assets/logo_fleetcare.svg';
import qm_mindy from '../assets/logo_mindy.svg';
import qm_oms from '../assets/logo_oms.svg';
import qm_flash from '../assets/logo_qm_flash_white.svg';
import logo_quadmindsAI from '../assets/logo_quadmindsAI.svg';
import qm_stork from '../assets/logo_tdc_white.svg';
import qm_torre_ghost from '../assets/torre-ghost.svg';
import CarouselComponent from '../components/CarouselComponent/CarouselComponent';
import {
    applyBrandFavicon,
    hasClaroParam,
    isBrandedPlatform,
    isFleetcare,
    isMindy,
    isOms,
    isStork,
    pickPlatformValue,
} from '../utils';

const resolveSideLogo = (): string => {
    switch (true) {
        case isMindy:
            return qm_mindy;
        case isOms:
            return qm_oms;
        case isFleetcare:
            return qm_fleetcare;
        case isStork:
            return qm_stork;
        default:
            return qm_flash;
    }
};

const resolveSlogan = (t: TFunction): string => {
    switch (true) {
        case isFleetcare:
            return t('default.fleetcareSlogan');
        case isMindy:
            return t('default.mindySlogan');
        case isStork:
            return t('default.storkSlogan');
        default:
            return t('default.slogan');
    }
};

// Branded (mindy / fleetcare / oms) layout: full screen background + glass form panel
const BrandedSignupLayout: React.FC<{ children: React.ReactNode; showClaro: boolean }> = ({ children, showClaro }) => (
    <motion.div
        className="min-h-screen relative"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
    >
        {/* Background - Full Screen */}
        <div
            className={clsx(
                'absolute inset-0 bg-cover bg-center',
                isMindy && MINDY_BG_CLASS,
                isFleetcare && FLEETCARE_BG_CLASS,
                isOms && OMS_BG_CLASS,
            )}
        ></div>

        {/* Right side - Form Container with Glass Effect */}
        <div className="relative ml-auto w-full lg:w-[480px] min-h-screen backdrop-blur-md lg:border-l border-gray-800">
            <div className="absolute inset-0 bg-[#1B1C1D] opacity-70 z-0"></div>
            {/* Logo Mindy - Top Left */}
            <motion.div
                className="absolute top-8 left-8 z-1"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
            >
                {isMindy && <img src={qm_mindy} alt="Mindy" className="h-8" />}
                {isFleetcare && <img src={qm_fleetcare} alt="Fleetcare" className="h-8" />}
            </motion.div>

            {/* Centered Content */}
            <div className="min-h-screen relative flex items-center justify-center px-12 py-20 z-1">
                <div className="w-full max-w-md">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key="content"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            {children}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>

            {/* Footer logos - Bottom Right */}
            <motion.div
                className="absolute bottom-8 right-8 flex items-center gap-4 z-1"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
            >
                {showClaro && <img src={logo_claro} alt="Claro" className="h-6 mb-1.5" />}
                <img src={logo_quadmindsAI} alt="QuadMinds AI" className="h-6" />
            </motion.div>
        </div>
    </motion.div>
);

const SignupLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const showClaro = hasClaroParam(searchParams);

    useEffect(() => {
        applyBrandFavicon();
    }, []);

    const leftSideClasses = clsx(
        'relative first-letter:hidden lg:flex lg:flex-col lg:justify-center lg:items-center w-full h-full bg-gradient-to-b',
        pickPlatformValue({
            branded: 'from-primary-600 to-primary-400',
            stork: 'from-variant-900 to-blue-800',
            fallback: 'from-blue-500 to-blue-800',
        }),
    );

    if (isBrandedPlatform) {
        return <BrandedSignupLayout showClaro={showClaro}>{children}</BrandedSignupLayout>;
    }

    return (
        <div className="grid grid-cols-1 gap-0 h-screen lg:grid-cols-2 font-['Roboto']">
            <motion.div
                className={leftSideClasses}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                {isStork && (
                    <motion.img
                        className="absolute left-0 bottom-0 h-full z-10 bg-image-mask opacity-10"
                        src={qm_torre_ghost}
                        alt="torre-ghost"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.1 }}
                        transition={{ delay: 0.5, duration: 1 }}
                    />
                )}
                <motion.div
                    className="z-20 flex flex-col items-center justify-center"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                >
                    <motion.img
                        src={resolveSideLogo()}
                        alt="logo"
                        className="mb-6 h-auto w-[350px] 2xl:w-[500px] object-cover"
                        initial={{ x: -100, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.8, ease: 'easeInOut' }}
                    />
                    <motion.h1
                        className="w-full text-center text-white text-xl 2xl:text-4xl xl:text-2xl font-medium mb-8"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: 'easeOut', delay: 0.5 }}
                    >
                        {resolveSlogan(t)}
                    </motion.h1>
                    <CarouselComponent />
                </motion.div>
            </motion.div>
            <div className="w-full h-screen bg-white">{children}</div>
        </div>
    );
};

export default SignupLayout;
